/**
 * OnlineGame - Partida online 1v1 en tiempo real
 * 501 Dart League Online
 * 
 * Este componente conecta la UI del juego X01 con el backend vía Socket.IO.
 * El servidor es AUTORITATIVO: toda tirada se envía al server, y el server
 * emite game:state-update con el estado real. La UI solo refleja lo que dice el server.
 * 
 * FLUJO:
 * 1. Recibe gameSetup (roomId, config, player1, player2) del lobby
 * 2. Emite game:join para unirse a la sala
 * 3. Renderiza marcador, teclado numérico, historial de rondas
 * 4. Al tirar: emite game:throw → espera game:state-update del server
 * 5. Al cerrar: server pide dardos → emite game:closing-darts
 * 6. Al acabar: muestra resultado con stats
 * 7. Desconexión del rival: muestra overlay con cuenta atrás de reconexión
 */

import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import socketService from './socketService';
import { getCheckoutOptions, formatCheckout } from './utils/checkouts';
import useWebRTC from './utils/useWebRTC';
import CameraSetup from './CameraSetup';
import VideoOverlay from './VideoOverlay';
import CheckoutConfirm from './CheckoutConfirm';
import './styles/online.css';
import './styles/x01game.css';
import './styles/video.css';
import SponsorsStrip from './SponsorsStrip';
import logo180 from '../../assets/logo180.png';
import logoApp from '../../assets/Logo26.png';
import ChatWidget from './ChatWidget';

// ============================================
// SUB-COMPONENTES (modales)
// ============================================

const OnlineDardosCierreModal = ({ visible, closingScore, onSelect }) => {
  if (!visible) return null;
  const canOneD = closingScore <= 50 && closingScore % 2 === 0;
  const canTwoD = closingScore <= 110;

  return (
    <div className="x01-modal-overlay">
      <div className="x01-modal x01-dardos-modal">
        <h2>¡Has cerrado!</h2>
        <p className="x01-modal-subtitle">
          Cerraste <strong>{closingScore}</strong>. ¿Con cuántos dardos?
        </p>
        <div className="x01-dardos-options">
          <button className={`x01-dardo-btn ${!canOneD ? 'disabled' : ''}`} onClick={() => canOneD && onSelect(1)} disabled={!canOneD}>
            <span className="x01-dardo-num">1</span>
            <span className="x01-dardo-label">dardo</span>
            {!canOneD && <span className="x01-dardo-impossible">Imposible</span>}
          </button>
          <button className={`x01-dardo-btn ${!canTwoD ? 'disabled' : ''}`} onClick={() => canTwoD && onSelect(2)} disabled={!canTwoD}>
            <span className="x01-dardo-num">2</span>
            <span className="x01-dardo-label">dardos</span>
            {!canTwoD && <span className="x01-dardo-impossible">Imposible</span>}
          </button>
          <button className="x01-dardo-btn" onClick={() => onSelect(3)}>
            <span className="x01-dardo-num">3</span>
            <span className="x01-dardo-label">dardos</span>
          </button>
        </div>
      </div>
    </div>
  );
};

const OnlineResultModal = ({ visible, data, onExit, rematchState, onRematchRequest }) => {
  if (!visible || !data) return null;
  const { winnerUsername, isForfeit, forfeitReason, finalState } = data;

  const p1 = finalState.player1;
  const p2 = finalState.player2;

  // Calcular promedios
  const calcAvg = (player) => {
    if (player.totalDarts === 0) return 0;
    return (player.totalScore / player.totalDarts) * 3;
  };

  const getPlayerLegRounds = (playerNum) => {
    const legHistory = Array.isArray(finalState.legHistory) ? finalState.legHistory : [];
    const roundsFromHistory = legHistory.map((leg) =>
      playerNum === 1 ? (leg.player1Rounds || []) : (leg.player2Rounds || [])
    );

    const currentLegRounds = Array.isArray(finalState[`player${playerNum}`]?.rounds)
      ? finalState[`player${playerNum}`].rounds
      : [];

    const currentLegInHistory = legHistory.some((leg) => leg.leg === finalState.currentLeg);

    if (!currentLegInHistory && currentLegRounds.length > 0) {
      roundsFromHistory.push(currentLegRounds);
    }

    if (roundsFromHistory.length === 0) {
      roundsFromHistory.push(currentLegRounds);
    }

    return roundsFromHistory;
  };

  const calcCheckoutStats = (playerNum) => {
    const gameMode = finalState.gameMode || 501;
    const legsRounds = getPlayerLegRounds(playerNum);
    let opportunities = 0;
    let successful = 0;
    let highFinish = 0;

    legsRounds.forEach((rounds) => {
      let scoreBeforeTurn = gameMode;

      rounds.forEach((round) => {
        if (getCheckoutOptions(scoreBeforeTurn).length > 0) {
          opportunities += 1;
        }

        if (round?.closing) {
          successful += 1;
          if (typeof round.score === 'number') {
            highFinish = Math.max(highFinish, round.score);
          }
          scoreBeforeTurn = 0;
          return;
        }

        if (!round?.bust && !round?.timeout && typeof round?.score === 'number') {
          scoreBeforeTurn = Math.max(0, scoreBeforeTurn - round.score);
        }
      });
    });

    const checkoutPct = opportunities > 0 ? (successful / opportunities) * 100 : 0;
    return { opportunities, successful, checkoutPct, highFinish };
  };

  const calcScoringStats = (playerNum) => {
    const legsRounds = getPlayerLegRounds(playerNum);
    let count100Plus = 0;
    let count140Plus = 0;
    let bestLegAverage = 0;

    legsRounds.forEach((rounds) => {
      let legScore = 0;
      let legDarts = 0;

      rounds.forEach((round) => {
        const dartsThrown = typeof round?.darts === 'number' ? round.darts : 3;
        legDarts += dartsThrown;

        const roundScore = !round?.bust && !round?.timeout && typeof round?.score === 'number'
          ? round.score
          : 0;

        legScore += roundScore;

        if (roundScore >= 100) count100Plus += 1;
        if (roundScore >= 140) count140Plus += 1;
      });

      if (legDarts > 0) {
        const legAverage = (legScore / legDarts) * 3;
        bestLegAverage = Math.max(bestLegAverage, legAverage);
      }
    });

    return { count100Plus, count140Plus, bestLegAverage };
  };

  const p1Checkout = calcCheckoutStats(1);
  const p2Checkout = calcCheckoutStats(2);
  const p1Scoring = calcScoringStats(1);
  const p2Scoring = calcScoringStats(2);

  return (
    <div className="x01-modal-overlay">
      <div className="x01-modal x01-result-modal">
        <div className="x01-result-trophy">{winnerUsername ? '🏆' : '🚫'}</div>
        <h2>{winnerUsername ? `¡${winnerUsername} gana la partida!` : 'Partida anulada'}</h2>
        {isForfeit && (
          <p className="online-forfeit-reason">
            {forfeitReason === 'disconnect' 
              ? 'Victoria por desconexión del rival' 
              : forfeitReason === 'turn_timeout'
              ? 'Victoria por tiempo agotado del rival'
              : forfeitReason === 'camera_timeout'
              ? 'Partida anulada: cámara no restaurada a tiempo'
              : 'Victoria por abandono del rival'}
          </p>
        )}

        <div className="x01-result-scoreboard">
          <div className={`x01-result-player ${finalState.winner === 1 ? 'winner' : ''}`}>
            <span className="x01-result-name">{p1.username}</span>
            <span className="x01-result-legs">{p1.legs}</span>
            {finalState.winner === 1 && <span className="x01-result-crown">👑</span>}
          </div>
          <div className={`x01-result-player ${finalState.winner === 2 ? 'winner' : ''}`}>
            <span className="x01-result-name">{p2.username}</span>
            <span className="x01-result-legs">{p2.legs}</span>
            {finalState.winner === 2 && <span className="x01-result-crown">👑</span>}
          </div>
        </div>

        <div className="x01-result-stats">
          <div className="x01-result-stat-row">
            <span className="x01-stat-label">Promedio (3 dardos)</span>
            <div className="x01-stat-values">
              <span className={`x01-stat-value ${finalState.winner === 1 ? 'highlight' : ''}`}>{calcAvg(p1).toFixed(2)}</span>
              <span className={`x01-stat-value ${finalState.winner === 2 ? 'highlight' : ''}`}>{calcAvg(p2).toFixed(2)}</span>
            </div>
          </div>
          <div className="x01-result-stat-row">
            <span className="x01-stat-label">Dardos totales</span>
            <div className="x01-stat-values">
              <span className="x01-stat-value">{p1.totalDarts}</span>
              <span className="x01-stat-value">{p2.totalDarts}</span>
            </div>
          </div>
          <div className="x01-result-stat-row">
            <span className="x01-stat-label">100+</span>
            <div className="x01-stat-values">
              <span className="x01-stat-value">{p1Scoring.count100Plus}</span>
              <span className="x01-stat-value">{p2Scoring.count100Plus}</span>
            </div>
          </div>
          <div className="x01-result-stat-row">
            <span className="x01-stat-label">140+</span>
            <div className="x01-stat-values">
              <span className="x01-stat-value">{p1Scoring.count140Plus}</span>
              <span className="x01-stat-value">{p2Scoring.count140Plus}</span>
            </div>
          </div>
          <div className="x01-result-stat-row">
            <span className="x01-stat-label">Mejor average (leg)</span>
            <div className="x01-stat-values">
              <span className="x01-stat-value">{p1Scoring.bestLegAverage.toFixed(2)}</span>
              <span className="x01-stat-value">{p2Scoring.bestLegAverage.toFixed(2)}</span>
            </div>
          </div>
          <div className="x01-result-stat-row">
            <span className="x01-stat-label">Checkout % (con alternativa real de salida)</span>
            <div className="x01-stat-values">
              <span className="x01-stat-value">{p1Checkout.checkoutPct.toFixed(1)}% ({p1Checkout.successful}/{p1Checkout.opportunities})</span>
              <span className="x01-stat-value">{p2Checkout.checkoutPct.toFixed(1)}% ({p2Checkout.successful}/{p2Checkout.opportunities})</span>
            </div>
          </div>
          <div className="x01-result-stat-row">
            <span className="x01-stat-label">Cierre más alto</span>
            <div className="x01-stat-values">
              <span className="x01-stat-value">{p1Checkout.highFinish || '-'}</span>
              <span className="x01-stat-value">{p2Checkout.highFinish || '-'}</span>
            </div>
          </div>
        </div>

        <div className="x01-result-actions">
          {rematchState === 'none' && (
            <button className="x01-result-btn primary" onClick={onRematchRequest}>
              🔄 Revancha
            </button>
          )}
          {rematchState === 'requesting' && (
            <button className="x01-result-btn primary" disabled>
              Esperando rival...
            </button>
          )}
          {rematchState === 'declined' && (
            <button className="x01-result-btn primary" disabled>
              Revancha rechazada
            </button>
          )}
          {rematchState === 'expired' && (
            <button className="x01-result-btn primary" disabled>
              Sin respuesta
            </button>
          )}
          {rematchState === 'rival-left' && (
            <button className="x01-result-btn primary" disabled>
              Tu rival se ha ido
            </button>
          )}
          <button className="x01-result-btn secondary" onClick={onExit}>
            Volver al Lobby
          </button>
        </div>
      </div>
    </div>
  );
};

const ConfirmModal = ({ visible, title, message, onConfirm, onCancel }) => {
  if (!visible) return null;
  return (
    <div className="x01-modal-overlay">
      <div className="x01-modal x01-confirm-modal">
        <h3>{title}</h3>
        <p>{message}</p>
        <div className="x01-confirm-actions">
          <button className="x01-result-btn secondary" onClick={onCancel}>Cancelar</button>
          <button className="x01-result-btn primary" onClick={onConfirm}>Confirmar</button>
        </div>
      </div>
    </div>
  );
};

// ============================================
// COMPONENTE PRINCIPAL
// ============================================

const OnlineGame = ({ gameSetup: initialGameSetup, currentUserId, onExit, onGameSetupRecovered, onRematch }) => {
  // Estado para manejar gameSetup que puede llegar tarde (tras rejoin)
  const [recoveredSetup, setRecoveredSetup] = useState(null);
  const [rejoinFailed, setRejoinFailed] = useState(false);

  const activeSetup = initialGameSetup || recoveredSetup;

  // ─── Recuperación tras refresh: pedir rejoin si no hay gameSetup ───
  useEffect(() => {
    if (initialGameSetup) return; // Ya tenemos datos, no hace falta rejoin

    // Conectar socket si no está conectado (el lobby lo conecta, pero tras refresh no hay lobby)
    if (!socketService.isConnected) {
      socketService.connect();
    }

    const handleRejoinResponse = (response) => {
      if (response.active && response.gameSetup) {
        setRecoveredSetup(response.gameSetup);
        if (onGameSetupRecovered) {
          onGameSetupRecovered(response.gameSetup);
        }
      } else {
        // No hay partida activa, volver al lobby
        setRejoinFailed(true);
      }
    };

    const cleanup = socketService.on('game:rejoin-response', handleRejoinResponse);

    // Esperar a que el socket esté conectado para emitir (máx 10 intentos)
    let retries = 0;
    const tryRejoin = () => {
      if (socketService.isConnected) {
        socketService.emit('game:rejoin');
      } else if (retries < 10) {
        retries++;
        setTimeout(tryRejoin, 500);
      } else {
        setRejoinFailed(true);
      }
    };
    tryRejoin();

    return cleanup;
  }, [initialGameSetup, onGameSetupRecovered]);

  // Si el rejoin falló, volver al lobby
  useEffect(() => {
    if (rejoinFailed) {
      onExit();
    }
  }, [rejoinFailed, onExit]);

  // Si no tenemos setup aún, mostrar loading
  if (!activeSetup) {
    return (
      <div className="online-game-loading">
        <div className="online-loading-spinner"></div>
        <p>Reconectando a la partida...</p>
        <button className="x01-result-btn secondary" onClick={onExit}>
          Salir al Lobby
        </button>
      </div>
    );
  }

  return <OnlineGameInner key={activeSetup.roomId} gameSetup={activeSetup} currentUserId={currentUserId} onExit={onExit} onRematch={onRematch} />;
};

const OnlineGameInner = ({ gameSetup, currentUserId, onExit, onRematch }) => {
  const { roomId, config, player1, player2 } = gameSetup;
  const { gameMode, legsToWin } = config;

  // ¿Soy player1 o player2?
  const myPlayerNum = currentUserId === player1.id ? 1 : 2;
  const playerNames = [player1.username, player2.username];
  const rivalName = myPlayerNum === 1 ? player2.username : player1.username;
  const myName = myPlayerNum === 1 ? player1.username : player2.username;

  // ─── Fase de camera setup ───
  const [cameraSetupDone, setCameraSetupDone] = useState(false);
  const [setupCancelledMsg, setSetupCancelledMsg] = useState(null);

  // ─── WebRTC hook ───
  const webrtc = useWebRTC({
    roomId,
    myPlayerNum,
    socketService,
    enabled: true,
  });

  // ─── Checkout confirm ───
  const [checkoutConfirm, setCheckoutConfirm] = useState({
    visible: false,
    isMe: false,
    playerName: '',
    closingScore: 0,
  });

  // Estado del juego (viene del servidor)
  const [gameState, setGameState] = useState(null);
  const [input, setInput] = useState('');
  const [show180, setShow180] = useState(false);
  const [alertMsg, setAlertMsg] = useState(null);

  // Modales
  const [dardosModal, setDardosModal] = useState({ visible: false, closingScore: 0 });
  const [resultModal, setResultModal] = useState({ visible: false, data: null });
  const [confirmModal, setConfirmModal] = useState({ visible: false, title: '', message: '', onConfirm: null });

  // Overlay de desconexión del rival
  const [rivalDisconnected, setRivalDisconnected] = useState(null); // { player, timeout }
  const [disconnectTimer, setDisconnectTimer] = useState(null);
  const disconnectIntervalRef = useRef(null);

  // Rematch
  const [rematchState, setRematchState] = useState('none'); // 'none' | 'requesting' | 'received' | 'declined' | 'expired'
  const [rematchRequest, setRematchRequest] = useState(null); // { player, expiresAt }

  // Undo request
  const [undoState, setUndoState] = useState('none'); // 'none' | 'requesting' | 'received'
  const [undoRequestData, setUndoRequestData] = useState(null); // { player, score, expiresAt }

  // Turn skip warning (timeout sin perder partida)
  const [turnSkipWarning, setTurnSkipWarning] = useState(null); // { message, isMe }

  // Turn timer countdown
  const [turnTimeLeft, setTurnTimeLeft] = useState(null);
  const turnTimerIntervalRef = useRef(null);
  const serverClockOffsetRef = useRef(0);

  // Ref para auto-scroll
  const roundsPanelRef = useRef(null);
  const mobileScrollRefs = useRef([]);

  // Para detectar 180s
  const prevRoundsLenRef = useRef({ p1: 0, p2: 0 });

  // Ref para acceder a gameState dentro de listeners sin causar re-registro
  const gameStateRef = useRef(null);

  // ─── Unirse a la sala al montar ───
  useEffect(() => {
    // Red de seguridad: reconectar si el socket se perdió
    if (!socketService.isConnected) {
      socketService.connect();
      const tryJoin = () => {
        if (socketService.isConnected) {
          socketService.emit('game:join', { roomId });
        } else {
          setTimeout(tryJoin, 500);
        }
      };
      tryJoin();
    } else {
      socketService.emit('game:join', { roomId });
    }
  }, [roomId]);

  // ─── Listeners de socket ───
  useEffect(() => {
    const handleStateUpdate = ({ state, timestamp }) => {
      if (Number.isFinite(timestamp)) {
        serverClockOffsetRef.current = timestamp - Date.now();
      }

      // Detectar 180 (comparar con estado anterior via ref)
      if (gameStateRef.current) {
        const prevP1Rounds = prevRoundsLenRef.current.p1;
        const prevP2Rounds = prevRoundsLenRef.current.p2;
        const newP1Rounds = state.player1.rounds.length;
        const newP2Rounds = state.player2.rounds.length;

        if (newP1Rounds > prevP1Rounds) {
          const lastRound = state.player1.rounds[newP1Rounds - 1];
          if (lastRound && lastRound.score === 180) {
            setShow180(true);
            setTimeout(() => setShow180(false), 2200);
          }
        }
        if (newP2Rounds > prevP2Rounds) {
          const lastRound = state.player2.rounds[newP2Rounds - 1];
          if (lastRound && lastRound.score === 180) {
            setShow180(true);
            setTimeout(() => setShow180(false), 2200);
          }
        }
      }

      prevRoundsLenRef.current = {
        p1: state.player1.rounds.length,
        p2: state.player2.rounds.length,
      };

      gameStateRef.current = state;
      setGameState(state);

      // Actualizar turn timer desde el deadline del server
      if (state.turnDeadline && state.phase === 'playing' && !state.disconnectedPlayer) {
        // Limpiar interval anterior
        if (turnTimerIntervalRef.current) clearInterval(turnTimerIntervalRef.current);
        
        const updateTimer = () => {
          const adjustedNow = Date.now() + serverClockOffsetRef.current;
          const remaining = Math.max(0, Math.ceil((state.turnDeadline - adjustedNow) / 1000));
          setTurnTimeLeft(remaining);
          if (remaining <= 0 && turnTimerIntervalRef.current) {
            clearInterval(turnTimerIntervalRef.current);
            turnTimerIntervalRef.current = null;
          }
        };
        updateTimer();
        turnTimerIntervalRef.current = setInterval(updateTimer, 1000);
      } else {
        setTurnTimeLeft(null);
        if (turnTimerIntervalRef.current) {
          clearInterval(turnTimerIntervalRef.current);
          turnTimerIntervalRef.current = null;
        }
      }

      // Si estamos en fase de awaiting-darts y es MI cierre → mostrar modal
      if (state.phase === 'awaiting-darts' && state.pendingClose) {
        if (state.pendingClose.playerNum === myPlayerNum) {
          // Calcular closingScore: es el score que el jugador tenía antes de la tirada
          setDardosModal({ visible: true, closingScore: state.pendingClose.score });
        }
      } else {
        setDardosModal({ visible: false, closingScore: 0 });
      }
    };

    const handleFinished = (data) => {
      setResultModal({
        visible: true,
        data: {
          winnerUsername: data.winnerUsername,
          isForfeit: data.forfeit || false,
          forfeitReason: data.forfeitReason || null,
          finalState: data.finalState,
        }
      });
    };

    const handleNewLeg = () => {
      // Reset input
      setInput('');
    };

    const handlePlayerDisconnected = ({ player, timeout }) => {
      setRivalDisconnected({ player, timeout });
      setDisconnectTimer(Math.ceil(timeout / 1000));

      // Countdown
      if (disconnectIntervalRef.current) clearInterval(disconnectIntervalRef.current);
      const startTime = Date.now();
      disconnectIntervalRef.current = setInterval(() => {
        const elapsed = Date.now() - startTime;
        const remaining = Math.max(0, Math.ceil((timeout - elapsed) / 1000));
        setDisconnectTimer(remaining);
        if (remaining <= 0) {
          clearInterval(disconnectIntervalRef.current);
          disconnectIntervalRef.current = null;
        }
      }, 1000);
    };

    const handlePlayerReconnected = () => {
      setRivalDisconnected(null);
      setDisconnectTimer(null);
      if (disconnectIntervalRef.current) {
        clearInterval(disconnectIntervalRef.current);
        disconnectIntervalRef.current = null;
      }
    };

    const handleGameError = ({ message }) => {
      setAlertMsg(message);
    };

    const handleTurnSkipped = ({ player, playerNum, message }) => {
      const isMe = playerNum === myPlayerNum;
      setTurnSkipWarning({
        message: isMe 
          ? '⚠️ No tiraste a tiempo. Tu turno se ha saltado. Si vuelve a ocurrir, perderás la partida.'
          : `${player} no tiró a tiempo. Su turno se ha saltado.`,
        isMe,
      });
      // Auto-ocultar tras 6 segundos (más tiempo si soy yo, para que lo lea)
      setTimeout(() => setTurnSkipWarning(null), isMe ? 8000 : 5000);
    };

    // ── Undo handlers ──
    const handleUndoRequested = (data) => {
      setUndoState('received');
      setUndoRequestData(data);
    };

    const handleUndoAccepted = () => {
      setUndoState('none');
      setUndoRequestData(null);
    };

    const handleUndoRejected = () => {
      setUndoState('none');
      setUndoRequestData(null);
      setAlertMsg('Petición de deshacer rechazada');
    };

    const handleUndoExpired = () => {
      setUndoState('none');
      setUndoRequestData(null);
      setAlertMsg('Sin respuesta a la petición de deshacer');
    };

    const handleUndoPending = () => {
      setUndoState('requesting');
    };

    // ── Rematch handlers ──
    const handleRematchRequested = (data) => {
      setRematchState('received');
      setRematchRequest(data);
    };

    const handleRematchDeclined = () => {
      setRematchState('declined');
    };

    const handleRematchExpired = () => {
      setRematchState('expired');
    };

    const handleRivalLeft = () => {
      setRematchState('rival-left');
      setRematchRequest(null);
    };

    const handleSetupCancelled = ({ message }) => {
      console.log('[OnlineGame] Rival canceló setup:', message);
      setSetupCancelledMsg(message || 'Tu rival ha cancelado la partida');
    };

    const handleRematchStarting = (newGameSetup) => {
      // Revancha aceptada → navegar a nueva partida
      if (onRematch) {
        onRematch(newGameSetup);
      }
    };

    // ── Checkout confirm handlers (video) ──
    const handleCheckoutPending = ({ playerNum, playerName, closingScore }) => {
      // El servidor nos pide que confirmemos un cierre
      const isMe = playerNum === myPlayerNum;
      setCheckoutConfirm({
        visible: true,
        isMe,
        playerName: playerName || playerNames[playerNum - 1],
        closingScore,
      });
    };

    const handleCheckoutConfirmed = () => {
      setCheckoutConfirm(prev => ({ ...prev, visible: false }));
    };

    const handleCheckoutDisputed = ({ message }) => {
      setCheckoutConfirm(prev => ({ ...prev, visible: false }));
      setAlertMsg(message || 'El cierre ha sido disputado. Resolved por chat.');
    };

    const cleanups = [
      socketService.on('game:state-update', handleStateUpdate),
      socketService.on('game:finished', handleFinished),
      socketService.on('game:new-leg', handleNewLeg),
      socketService.on('game:player-disconnected', handlePlayerDisconnected),
      socketService.on('game:player-reconnected', handlePlayerReconnected),
      socketService.on('game:error', handleGameError),
      socketService.on('game:undo-requested', handleUndoRequested),
      socketService.on('game:undo-accepted', handleUndoAccepted),
      socketService.on('game:undo-rejected', handleUndoRejected),
      socketService.on('game:undo-expired', handleUndoExpired),
      socketService.on('game:undo-pending', handleUndoPending),
      socketService.on('game:rematch-requested', handleRematchRequested),
      socketService.on('game:rematch-declined', handleRematchDeclined),
      socketService.on('game:rematch-expired', handleRematchExpired),
      socketService.on('game:rematch-starting', handleRematchStarting),
      socketService.on('game:rival-left', handleRivalLeft),
      socketService.on('game:setup-cancelled', handleSetupCancelled),
      socketService.on('game:turn-skipped', handleTurnSkipped),
      socketService.on('game:checkout-pending', handleCheckoutPending),
      socketService.on('game:checkout-confirmed', handleCheckoutConfirmed),
      socketService.on('game:checkout-disputed', handleCheckoutDisputed),
    ];

    return () => {
      cleanups.forEach(cleanup => typeof cleanup === 'function' && cleanup());
      if (disconnectIntervalRef.current) {
        clearInterval(disconnectIntervalRef.current);
      }
      if (turnTimerIntervalRef.current) {
        clearInterval(turnTimerIntervalRef.current);
      }
    };
  }, [myPlayerNum, roomId]); // eslint-disable-line react-hooks/exhaustive-deps

  // ─── Auto-scroll ───
  useEffect(() => {
    const el = roundsPanelRef.current;
    if (el) requestAnimationFrame(() => { el.scrollTop = el.scrollHeight; });
    mobileScrollRefs.current.forEach(ref => {
      if (ref) requestAnimationFrame(() => { ref.scrollLeft = ref.scrollWidth; });
    });
  }, [gameState]);

  // ─── Alert auto-dismiss ───
  useEffect(() => {
    if (alertMsg) {
      const timer = setTimeout(() => setAlertMsg(null), 3000);
      return () => clearTimeout(timer);
    }
  }, [alertMsg]);

  // ─── Derived state ───
  const isCameraSetupPhase = gameState?.phase === 'camera-setup';
  const isMyTurn = gameState?.activePlayer === myPlayerNum && gameState?.phase === 'playing' && undoState === 'none';
  const scores = useMemo(
    () => gameState ? [gameState.player1.score, gameState.player2.score] : [gameMode, gameMode],
    // Deps granulares: solo recalcular cuando cambian los scores, no en cada update de gameState
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [gameState?.player1?.score, gameState?.player2?.score, gameMode]
  );
  const matchesWon = gameState ? [gameState.player1.legs, gameState.player2.legs] : [0, 0];
  const p1Rounds = gameState?.player1.rounds || [];
  const p2Rounds = gameState?.player2.rounds || [];
  const maxRounds = Math.max(p1Rounds.length, p2Rounds.length, 0);

  // Promedios en tiempo real
  const currentAverages = useMemo(() => {
    if (!gameState) return [0, 0];
    const calcAvg = (p) => p.totalDarts === 0 ? 0 : (p.totalScore / p.totalDarts) * 3;
    return [calcAvg(gameState.player1), calcAvg(gameState.player2)];
  }, [gameState]);

  // Checkout suggestion para mí
  const checkoutSuggestion = useMemo(() => {
    if (!isMyTurn || input !== '') return null;
    const myScore = scores[myPlayerNum - 1];
    if (myScore > 170 || myScore < 2) return null;
    const options = getCheckoutOptions(myScore);
    if (options.length === 0) return null;
    return formatCheckout(options[0]);
  }, [isMyTurn, scores, myPlayerNum, input]);

  // ─── Acciones del juego ───
  const handleNumpad = useCallback((digit) => {
    setInput(prev => {
      if (prev.length >= 3) return prev;
      return prev + digit;
    });
  }, []);

  const handleClear = useCallback(() => {
    if (input === '') {
      // BUST: enviar 0
      socketService.emit('game:throw', { roomId, score: 0 });
    } else {
      setInput('');
    }
  }, [input, roomId]);

  const handleOk = useCallback(() => {
    if (input === '' || !isMyTurn) return;
    const points = parseInt(input, 10);
    if (isNaN(points) || points < 0 || points > 180) {
      setAlertMsg('Puntuación inválida. Máximo 180.');
      setInput('');
      return;
    }
    socketService.emit('game:throw', { roomId, score: points });
    setInput('');
  }, [input, isMyTurn, roomId]);

  // ─── Teclado físico ───
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (dardosModal.visible || resultModal.visible || confirmModal.visible) return;
      if (!isMyTurn) return;

      if (e.key >= '0' && e.key <= '9') {
        e.preventDefault();
        handleNumpad(e.key);
      } else if (e.key === 'Enter') {
        e.preventDefault();
        handleOk();
      } else if (e.key === 'Backspace' || e.key === 'Delete') {
        e.preventDefault();
        handleClear();
      } else if (e.key === 'Escape') {
        e.preventDefault();
        setInput('');
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isMyTurn, dardosModal.visible, resultModal.visible, confirmModal.visible, handleNumpad, handleOk, handleClear]);

  const handleDardosCierre = (numDarts) => {
    socketService.emit('game:closing-darts', { roomId, darts: numDarts });
    setDardosModal({ visible: false, closingScore: 0 });
  };

  const handleUndo = () => {
    if (undoState !== 'none') return; // Ya hay petición pendiente
    socketService.emit('game:undo-request', { roomId });
  };

  const handleUndoAcceptAction = () => {
    socketService.emit('game:undo-accept', { roomId });
    setUndoState('none');
    setUndoRequestData(null);
  };

  const handleUndoRejectAction = () => {
    socketService.emit('game:undo-reject', { roomId });
    setUndoState('none');
    setUndoRequestData(null);
  };

  const handleForfeit = () => {
    setConfirmModal({
      visible: true,
      title: 'Abandonar partida',
      message: '¿Seguro que quieres abandonar? Tu rival ganará automáticamente.',
      onConfirm: () => {
        socketService.emit('game:forfeit', { roomId });
        setConfirmModal({ visible: false, title: '', message: '', onConfirm: null });
      }
    });
  };

  const handleExit = () => {
    // Si la partida no ha terminado, confirmar
    if (gameState && gameState.phase !== 'finished') {
      handleForfeit();
    } else {
      // Limpiar WebRTC (parar cámara, cerrar peer)
      webrtc.cleanup();
      // Notificar al servidor que salimos de la sala
      socketService.emit('game:leave-room', { roomId });
      onExit();
    }
  };

  // ─── Rematch ───
  const handleRematchRequest = () => {
    setRematchState('requesting');
    socketService.emit('game:rematch-request', { roomId });
  };

  const handleRematchAccept = () => {
    socketService.emit('game:rematch-accept', { roomId });
  };

  const handleRematchDecline = () => {
    setRematchRequest(null);
    setRematchState('none');
    socketService.emit('game:rematch-decline', { roomId });
  };

  const handleCameraSetupReady = useCallback(() => {
    setCameraSetupDone(true);
  }, []);

  // ─── Loading state ───
  if (!gameState) {
    return (
      <div className="x01-game-container">
        <div className="online-game-loading">
          <div className="online-game-loading-spinner"></div>
          <p>Conectando a la partida...</p>
          <p className="online-game-loading-players">
            {player1.username} vs {player2.username}
          </p>
          <button className="x01-result-btn secondary" onClick={handleExit}>
            Salir al Lobby
          </button>
        </div>
      </div>
    );
  }

  // ─── Camera Setup (pre-partida) ───
  if (!cameraSetupDone) {
    // Rival canceló el setup → mostrar mensaje amigable
    if (setupCancelledMsg) {
      return (
        <div className="x01-game-container">
          <div className="online-game-loading">
            <div className="x01-result-trophy">🚫</div>
            <h2>Partida cancelada</h2>
            <p style={{ marginTop: '12px', color: '#ccc', fontSize: '16px' }}>
              {setupCancelledMsg}
            </p>
            <button
              className="x01-result-btn secondary"
              style={{ marginTop: '24px' }}
              onClick={() => {
                webrtc.cleanup();
                onExit();
              }}
            >
              Volver al Lobby
            </button>
          </div>
        </div>
      );
    }

    return (
      <CameraSetup
        webrtc={webrtc}
        player1={player1}
        player2={player2}
        myPlayerNum={myPlayerNum}
        onReady={handleCameraSetupReady}
        onCancel={(reason) => {
          console.log('[OnlineGame] Camera setup cancelado:', reason);
          webrtc.cleanup();
          socketService.emit('game:leave-room', { roomId });
          onExit();
        }}
      />
    );
  }

  // ─── Numpad ───
  const numpadKeys = [
    ['1', '2', '3'],
    ['4', '5', '6'],
    ['7', '8', '9'],
    [input.length > 0 ? '←' : 'BUST', '0', 'OK'],
  ];

  // ─── Rondas como array de scores (para renderizar tabla) ───
  const allRounds = [p1Rounds, p2Rounds];

  return (
    <div className="x01-game-container">

      {/* Checkout Confirm (confirmación de cierre por el rival) */}
      <CheckoutConfirm
        visible={checkoutConfirm.visible}
        isMe={checkoutConfirm.isMe}
        playerName={checkoutConfirm.playerName}
        closingScore={checkoutConfirm.closingScore}
        onConfirm={() => {
          socketService.emit('game:checkout-confirm', { roomId, confirmed: true });
          setCheckoutConfirm(prev => ({ ...prev, visible: false }));
        }}
        onDispute={() => {
          socketService.emit('game:checkout-confirm', { roomId, confirmed: false });
          setCheckoutConfirm(prev => ({ ...prev, visible: false }));
          setAlertMsg('Has disputado el cierre. Usad el chat para resolver.');
        }}
        onTimeout={() => {
          // Solo el rival debe emitir. Si isMe=true el server auto-confirma con su timeout.
          if (!checkoutConfirm.isMe) {
            socketService.emit('game:checkout-confirm', { roomId, confirmed: true });
          }
          setCheckoutConfirm(prev => ({ ...prev, visible: false }));
        }}
      />

      {/* Alert */}
      {alertMsg && (
        <div className="x01-alert">
          <span>{alertMsg}</span>
        </div>
      )}

      {/* 180 Banner */}
      {show180 && (
        <div className="x01-180-banner">
          <img src={logo180} alt="180!" className="x01-180-img" />
        </div>
      )}

      {/* Banner de aviso por turno saltado */}
      {turnSkipWarning && (
        <div className={`online-turn-skip-banner ${turnSkipWarning.isMe ? 'warning-me' : 'warning-rival'}`}>
          <div className="online-turn-skip-content">
            <span className="online-turn-skip-icon">{turnSkipWarning.isMe ? '⚠️' : 'ℹ️'}</span>
            <span className="online-turn-skip-text">{turnSkipWarning.message}</span>
          </div>
          <button className="online-turn-skip-dismiss" onClick={() => setTurnSkipWarning(null)}>✕</button>
        </div>
      )}

      {/* Overlay de rival desconectado */}
      {rivalDisconnected && (
        <div className="online-disconnect-overlay">
          <div className="online-disconnect-box">
            <div className="online-disconnect-icon">⚡</div>
            <h3>{rivalDisconnected.player} se ha desconectado</h3>
            <p>Esperando reconexión...</p>
            <div className="online-disconnect-timer">{disconnectTimer}s</div>
            <p className="online-disconnect-hint">Si no vuelve, ganarás automáticamente</p>
            <button className="x01-result-btn secondary" onClick={handleExit}>
              Salir al Lobby
            </button>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="x01-game-header">
        <button className="x01-back-btn" onClick={handleExit}>← Salir</button>
        <div className="x01-match-info">
          <span className="x01-match-mode">{gameMode}</span>
          <span className="x01-match-legs">Primero a {legsToWin}</span>
          <span className="online-game-live-badge">🔴 EN VIVO</span>
        </div>
      </div>

      {/* Turn indicator */}
      <div className={`online-turn-indicator ${isCameraSetupPhase ? 'setup' : isMyTurn ? 'my-turn' : 'rival-turn'} ${turnTimeLeft != null && turnTimeLeft <= 15 ? 'urgent' : ''} ${turnTimeLeft != null && turnTimeLeft <= 5 ? 'critical' : ''}`}>
        <span className="online-turn-text">
          {isCameraSetupPhase ? '📹 Configurando cámaras...' : isMyTurn ? '🎯 Tu turno' : `⏳ Turno de ${playerNames[gameState.activePlayer - 1]}`}
        </span>
        {turnTimeLeft != null && (
          <span className={`online-turn-timer ${turnTimeLeft <= 15 ? 'urgent' : ''} ${turnTimeLeft <= 5 ? 'critical' : ''}`}>
            {turnTimeLeft}s
          </span>
        )}
      </div>

      {/* Marcador de legs */}
      <div className="x01-legs-display">
        {playerNames.map((name, i) => (
          <div key={i} className={`x01-leg-counter ${gameState.activePlayer === i + 1 ? 'active' : ''} ${i + 1 === myPlayerNum ? 'is-me' : ''}`}>
            <span className="x01-leg-name">
              {name} {i + 1 === myPlayerNum && <span className="online-me-badge">(Tú)</span>}
            </span>
            <span className="x01-leg-count">{matchesWon[i]}</span>
          </div>
        ))}
      </div>

      {/* Game Body - 3 columnas en desktop */}
      <div className="x01-game-body">
        {/* Panel de rondas */}
        <div className="x01-rounds-panel">
          <div className="x01-rounds-app-logo-wrap">
            <img src={logoApp} alt="501 Dart League" className="x01-rounds-app-logo" />
          </div>

          {/* Desktop */}
          <div className="x01-rounds-desktop">
            <div className="x01-rounds-header">
              <span className="x01-rounds-label">#</span>
              {playerNames.map((name, i) => (
                <span key={i} className={`x01-rounds-player ${gameState.activePlayer === i + 1 ? 'active' : ''}`}>{name}</span>
              ))}
            </div>
            <div className="x01-rounds-body" ref={roundsPanelRef}>
              {maxRounds > 0 ? (
                Array.from({ length: maxRounds }, (_, roundIdx) => (
                  <div key={roundIdx} className={`x01-round-row ${roundIdx === maxRounds - 1 ? 'latest' : ''}`}>
                    <span className="x01-round-num">{roundIdx + 1}</span>
                    {allRounds.map((playerRounds, pi) => {
                      const round = playerRounds[roundIdx];
                      const val = round?.score;
                      const isBust = round?.bust;
                      const isTimeout = round?.timeout;
                      const cls = val === 180 ? 'one-eighty' : val >= 100 ? 'ton' : isBust ? 'bust' : isTimeout ? 'timeout' : '';
                      return (
                        <span key={pi} className={`x01-round-score ${cls}`}>
                          {round ? (isBust ? 'BUST' : isTimeout ? 'T.O.' : val) : ''}
                        </span>
                      );
                    })}
                  </div>
                ))
              ) : (
                <p className="x01-rounds-empty">Sin rondas</p>
              )}
            </div>
          </div>

          {/* Mobile */}
          <div className="x01-rounds-mobile">
            {playerNames.map((name, pi) => {
              const rounds = allRounds[pi];
              return (
                <div key={pi} className="x01-rounds-player-row">
                  <div className={`x01-rounds-player-label ${gameState.activePlayer === pi + 1 ? 'active' : ''}`}>
                    <span>{name}</span>
                  </div>
                  <div className="x01-rounds-player-scroll" ref={el => { mobileScrollRefs.current[pi] = el; }}>
                    {rounds.map((round, idx) => {
                      const isLast = idx === rounds.length - 1;
                      const val = round.score;
                      const isBust = round.bust;
                      const isTimeout = round.timeout;
                      const cls = val === 180 ? 'one-eighty' : val >= 100 ? 'ton' : isBust ? 'bust' : isTimeout ? 'timeout' : '';
                      return (
                        <span key={idx} className={`x01-rounds-pill ${cls} ${isLast ? 'latest' : ''}`}>
                          R{idx + 1}.{isBust ? 'BUST' : isTimeout ? 'T.O.' : val}
                        </span>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Panel central: Cámaras + Scores + Input */}
        <div className="x01-center-panel">

          {/* Cámaras en directo (encima de los marcadores) */}
          {cameraSetupDone && (
            <VideoOverlay
              localVideoRef={webrtc.localVideoRef}
              remoteVideoRef={webrtc.remoteVideoRef}
              localStreamRef={webrtc.localStreamRef}
              phase={webrtc.phase}
              myAudioMuted={webrtc.myAudioMuted}
              rivalAudioMuted={webrtc.rivalAudioMuted}
              cameraLost={webrtc.cameraLost}
              toggleMyAudio={webrtc.toggleMyAudio}
              toggleRivalAudio={webrtc.toggleRivalAudio}
              remoteHasAudio={webrtc.remoteHasAudio}
              audioAutoplayBlocked={webrtc.audioAutoplayBlocked}
              retryRemoteAudio={webrtc.retryRemoteAudio}
              rivalName={rivalName}
              myName={myName}
            />
          )}

          {/* Marcadores */}
          <div className="x01-scores-display">
            {playerNames.map((name, i) => (
              <div key={i} className={`x01-score-panel ${gameState.activePlayer === i + 1 ? 'active' : ''}`}>
                <div className="x01-score-number">{scores[i]}</div>
                <div className="x01-score-avg">AVG: {currentAverages[i].toFixed(1)}</div>
              </div>
            ))}
          </div>

        </div>

        {/* Panel derecho: Input + Numpad */}
        <div className="x01-right-panel">
          {/* Input / Checkout hints encima del teclado */}
          <div className="x01-input-section x01-input-section-right">
            {isCameraSetupPhase ? (
              <div className="online-waiting-turn">
                <div className="online-waiting-dots">
                  <span></span><span></span><span></span>
                </div>
                <p>Estableciendo conexión de video...</p>
              </div>
            ) : isMyTurn ? (
              <>
                <div className="x01-input-display">
                  {input === '' && checkoutSuggestion ? (
                    <span className="x01-checkout-hint-big">{checkoutSuggestion}</span>
                  ) : (
                    <span className="x01-input-value">{input || '\u2014'}</span>
                  )}
                </div>
                <button className="x01-undo-btn" onClick={handleUndo} disabled={undoState === 'requesting'}>
                  {undoState === 'requesting' ? '⏳ Esperando...' : '↩ Deshacer'}
                </button>
              </>
            ) : (
              <div className="online-waiting-turn">
                <div className="online-waiting-dots">
                  <span></span><span></span><span></span>
                </div>
                <p>Esperando tirada del rival...</p>
              </div>
            )}
          </div>

          {/* Numpad */}
          <div className={`x01-numpad ${!isMyTurn ? 'disabled' : ''}`}>
          {numpadKeys.map((row, ri) => (
            <div key={ri} className="x01-numpad-row">
              {row.map(key => {
                const isBack = key === '←';
                const isBust = key === 'BUST';
                const isOK = key === 'OK';
                let cls = 'x01-numpad-key';
                if (isBack || isBust) cls += ' clear';
                if (isOK) cls += ' ok';
                return (
                  <button
                    key={key}
                    className={cls}
                    disabled={!isMyTurn}
                    onClick={() => {
                      if (!isMyTurn) return;
                      if (isOK) handleOk();
                      else if (isBack) setInput(prev => prev.slice(0, -1));
                      else if (isBust) handleClear();
                      else handleNumpad(key);
                    }}
                  >
                    {key}
                  </button>
                );
              })}
            </div>
          ))}
          </div>
        </div>
      </div>

      {/* Sponsors */}
      <SponsorsStrip />

      {/* Keyboard hint */}
      <div className="x01-keyboard-hint">
        Teclado: 0-9 · Enter = OK · Backspace = Borrar
      </div>

      {/* Modales */}
      <OnlineDardosCierreModal
        visible={dardosModal.visible}
        closingScore={dardosModal.closingScore}
        onSelect={handleDardosCierre}
      />

      <OnlineResultModal
        visible={resultModal.visible}
        data={resultModal.data}
        onExit={() => {
          webrtc.cleanup();
          socketService.emit('game:leave-room', { roomId });
          onExit();
        }}
        rematchState={rematchState}
        onRematchRequest={handleRematchRequest}
      />

      {/* Modal de revancha recibida */}
      {rematchRequest && rematchState === 'received' && (
        <div className="x01-modal-overlay">
          <div className="x01-modal x01-confirm-modal">
            <h3>🔄 Petición de Revancha</h3>
            <p><strong>{rematchRequest.player}</strong> quiere la revancha. ¿Aceptas?</p>
            <div className="x01-confirm-actions">
              <button className="x01-result-btn secondary" onClick={handleRematchDecline}>Rechazar</button>
              <button className="x01-result-btn primary" onClick={handleRematchAccept}>¡Acepto!</button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de petición de deshacer recibida */}
      {undoRequestData && undoState === 'received' && (
        <div className="x01-modal-overlay">
          <div className="x01-modal x01-confirm-modal">
            <h3>↩ Petición de Deshacer</h3>
            <p><strong>{undoRequestData.player}</strong> pide deshacer la última tirada ({undoRequestData.score} pts). ¿Aceptas?</p>
            <div className="x01-confirm-actions">
              <button className="x01-result-btn secondary" onClick={handleUndoRejectAction}>Rechazar</button>
              <button className="x01-result-btn primary" onClick={handleUndoAcceptAction}>Aceptar</button>
            </div>
          </div>
        </div>
      )}

      <ConfirmModal
        visible={confirmModal.visible}
        title={confirmModal.title}
        message={confirmModal.message}
        onConfirm={confirmModal.onConfirm}
        onCancel={() => setConfirmModal({ visible: false, title: '', message: '', onConfirm: null })}
      />

      <ChatWidget
        scope="game"
        roomId={roomId}
        currentUserId={currentUserId}
        title="Chat partida"
        quickMessages={['ok', 'espera', 'buena', '1 min', 'gracias', 'suerte']}
      />
    </div>
  );
};

export default OnlineGame;
